import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Alert, Button } from 'reactstrap';
import Content from '../../../layout/Content';
import { DRIVER_ACTIVITY_PATH } from '../../../../config/paths';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import {
  clearDriverActivity,
  getMonthlyDriverActivity,
} from '../../../../actions/traffic/DriverActivity';
import DriverActivitySearchForm from './DriverActivitySearchForm';
import Table from '../../../common/Table';
import Loader from '../../../common/Loader';
import generateMonthlyActivityColumns from './columns/driverActivityColumns';
import Modal from '../../../common/modal/Modal';
import DriverMovementHistoryForm from './DriverMovementHistoryForm';
import { postDriverMovementHistory } from '../../../../actions';
import { tzNormalizeDate } from '../../../../utils/date';

const DriverActivities = ({
  breadcrumbs,
  monthlyDriverActivities,
  loading,
  dispatchClearDriverActivity,
  dispatchGetMonthlyDriverActivity,
  dispatchPostDriverMovementHistory,
}) => {
  const [columns, setColumns] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [tableKey, setTableKey] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(
    () => () => {
      dispatchClearDriverActivity();
    },
    [],
  );

  const handleCreateActivity = useCallback((cell, date) => {
    const startDateTime = tzNormalizeDate({ date, format: 'YYYY-MM-DDT00:00' });
    const endDateTime = tzNormalizeDate({ date, format: 'YYYY-MM-DDT23:59' });

    setInitialValues({
      driver: {
        value: cell.driverId,
        label: cell.fullName,
      },
      startDate: startDateTime,
      endDate: endDateTime,
    });

    setModalOpen(true);
  }, []);

  const handleCounterClick = useCallback((dateKey, filterType) => {
    if (!monthlyDriverActivities || monthlyDriverActivities.length === 0) return;

    const filtered = monthlyDriverActivities.filter((driver) => {
      if (!driver.dayEventTypes) return false;

      const dayEventType = driver.dayEventTypes[dateKey] || 'EMPTY';
      const eventTypes = dayEventType.split('|');

      const hasItinerary = eventTypes.includes('ITINERARY');
      const hasReten = eventTypes.includes('RETEN');
      const hasReserve = eventTypes.includes('RESERVE');
      const hasMaintenance = eventTypes.includes('MAINTENANCE');
      const hasMovement = eventTypes.includes('MOVEMENT');
      const hasFreeArrivedLima = eventTypes.includes('FREE_ARRIVED_LIMA');

      switch (filterType) {
        case 'ON_TRIP':
          return hasItinerary;

        case 'ON_HOLD':
          return hasReten && !hasItinerary;

        case 'MAINTENANCE':
          return hasMaintenance && !hasItinerary && !hasReten;

        case 'RESERVE':
          return hasReserve && !hasItinerary && !hasReten && !hasMaintenance;

        case 'UNAVAILABLE':
          return hasMovement && !hasItinerary && !hasReten && !hasMaintenance;

        case 'FREE':
          return hasFreeArrivedLima && !hasItinerary && !hasReten && !hasMaintenance && !hasMovement;

        case 'NO_ACTIVITY':
          return !hasItinerary && !hasReten && !hasMaintenance && !hasMovement && !hasFreeArrivedLima;

        default:
          return false;
      }
    });

    setFilteredData(filtered);
    setActiveFilter({ dateKey, filterType });
    setTableKey((prev) => prev + 1);
  }, [monthlyDriverActivities]);

  const clearFilter = useCallback(() => {
    setFilteredData([]);
    setActiveFilter(null);
    setTableKey((prev) => prev + 1);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setInitialValues(null);
  }, []);

  const refreshTable = useCallback(() => {
    if (lastSearchParams) {
      dispatchGetMonthlyDriverActivity(lastSearchParams);
    }
  }, [lastSearchParams, dispatchGetMonthlyDriverActivity]);

  useEffect(() => {
    if (monthlyDriverActivities && monthlyDriverActivities.length > 0) {
      const dynamicColumns = generateMonthlyActivityColumns(
        monthlyDriverActivities,
        handleCreateActivity,
        handleCounterClick,
        activeFilter,
      );
      setColumns(dynamicColumns);
      setShowTable(true);
    } else {
      setShowTable(false);
      setFilteredData([]);
      setActiveFilter(null);
    }
  }, [monthlyDriverActivities, activeFilter, handleCreateActivity, handleCounterClick]);

  const onSubmit = useCallback((formValues) => {
    const newFormValues = {
      fromDateString: tzNormalizeDate({ date: formValues.fromDateString, format: 'YYYY-MM-DD' }),
      toDateString: tzNormalizeDate({ date: formValues.toDateString, format: 'YYYY-MM-DD' }),
    }

    if (formValues.homeBaseLocation && formValues.homeBaseLocation.length > 0) {
      newFormValues.homeBaseLocationIds = formValues.homeBaseLocation.map(
        (location) => location.value,
      );
    }

    if (formValues.driverStatusType && formValues.driverStatusType.length > 0) {
      newFormValues.driverStatusTypes = formValues.driverStatusType.map(
        (status) => status.value,
      );
    }

    setLastSearchParams(newFormValues);
    dispatchGetMonthlyDriverActivity(newFormValues);
    setTableKey((prev) => prev + 1);
  }, [dispatchGetMonthlyDriverActivity]);

  const onSubmitMovementHistoryForm = async (formValues) => {
    const newFormValues = {
      ...formValues,
      startDate: tzNormalizeDate({ date: formValues.startDate }),
      endDate: tzNormalizeDate({ date: formValues.endDate }),
      redirect: false,
      chargeMonth: formValues.month ? formValues.month.value : null,
      chargeYear: formValues.year || null
    };

    setActivityLoading(true);
    await dispatchPostDriverMovementHistory(newFormValues);

    setActivityLoading(false);

    closeModal();
    refreshTable();
  }

  const getFilterLabel = useCallback(() => {
    if (!activeFilter) return '';

    const [year, month, day] = activeFilter.dateKey.split('-');
    const date = `${day}/${month}/${year}`;

    const filterLabels = {
      ON_TRIP: 'En Viaje',
      ON_HOLD: 'En Retén',
      MAINTENANCE: 'Mantenimiento',
      RESERVE: 'Reserva',
      UNAVAILABLE: 'No Disponibles',
      FREE: 'Provincia - Lima',
      NO_ACTIVITY: 'Disponibles',
    };

    return `${filterLabels[activeFilter.filterType]} - ${date}`;
  }, [activeFilter]);

  const tableData = useMemo(
    () => (activeFilter ? filteredData : monthlyDriverActivities || []),
    [activeFilter, filteredData, monthlyDriverActivities],
  );

  const defaultFilterMethod = useCallback((filter, row) => {
    const id = filter.pivotId || filter.id;
    const value = row[id];

    if (value == null) return false;

    return String(value)
      .toLowerCase()
      .includes(String(filter.value).toLowerCase());
  }, []);

  const content = (
    <>
      <Modal
        id="driver-movement-history-modal"
        title="Crear Actividad de Conductor"
        body={
          <DriverMovementHistoryForm
            onSubmit={onSubmitMovementHistoryForm}
            initialValues={initialValues}
            isEdit={false}
            disableSaveButton={activityLoading}
          />
        }
        show={modalOpen}
        onClickCancel={closeModal}
        onClickClose={closeModal}
      />

      <DriverActivitySearchForm onSubmit={onSubmit} initialValues={{
        fromDateString: tzNormalizeDate(),
        toDateString: tzNormalizeDate(),
      }} />

      {activeFilter && (
        <Alert color="info" className="mt-3 d-flex justify-content-between align-items-center">
          <span>
            <i className="fa fa-filter mr-2" />
            Mostrando: <strong>{getFilterLabel()}</strong> ({filteredData.length}{' '}
            conductores)
          </span>
          <Button color="secondary" size="sm" onClick={clearFilter}>
            <i className="fa fa-times mr-1" />
            Limpiar filtro
          </Button>
        </Alert>
      )}

      {showTable && (
        <Table
          key={tableKey}
          columns={columns}
          data={tableData}
          loading={loading}
          manual={false}
          filterable
          defaultFilterMethod={defaultFilterMethod}
        />
      )}

      {!showTable && !loading && (
        <div className="alert alert-info mt-3">
          <i className="fa fa-info-circle" /> Seleccione un mes y año para ver el
          reporte
        </div>
      )}

      {loading && !showTable && <Loader />}
    </>
  );

  return (
    <Content
      title="Tareo de Conductores"
      breadcrumbs={breadcrumbs}
      content={content}
    />
  );
};

DriverActivities.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  monthlyDriverActivities: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    driverId: PropTypes.number,
    dayEventTypes: PropTypes.objectOf(PropTypes.string),
  })),
  loading: PropTypes.bool.isRequired,
  dispatchClearDriverActivity: PropTypes.func.isRequired,
  dispatchGetMonthlyDriverActivity: PropTypes.func.isRequired,
  dispatchPostDriverMovementHistory: PropTypes.func.isRequired,
};

DriverActivities.defaultProps = {
  monthlyDriverActivities: [],
};

const mapStateToProps = ({ TrafficUnit }) => ({
  breadcrumbs: [
    ...TrafficUnit.DriverUnitHome.get('breadcrumbs'),
    {
      text: 'Tareo de Conductores',
      href: DRIVER_ACTIVITY_PATH,
    },
  ],
  monthlyDriverActivities: TrafficUnit.DriverActivity.getIn([
    'all',
    'content',
    'content',
  ]),
  loading: TrafficUnit.DriverActivity.getIn(['all', 'loading']),
});

const mapDispatchToProps = {
  dispatchClearDriverActivity: clearDriverActivity,
  dispatchGetMonthlyDriverActivity: getMonthlyDriverActivity,
  dispatchPostDriverMovementHistory: postDriverMovementHistory,
};

export default connect(mapStateToProps, mapDispatchToProps)(DriverActivities);